/**
 * @file BootstrapProvider.jsx
 * @description
 * Provider de inicialización (bootstrap) de la aplicación.
 *
 * Responsabilidades:
 *  - Configurar baseURL de servicios HTTP.
 *  - Cargar configuración remota (envpaths.json).
 *  - Validar autenticación/token.
 *  - Cargar profile + company config.
 *  - Construir rutas dinámicas según scopes.
 *  - Registrar licencia MUI X Pro.
 *  - Exponer initializeSession vía Context.
 *  - Mostrar LoaderScreen mientras se inicializa.
 *
 * Este componente actúa como "orquestador" de arranque del sistema.
 */

import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateBaseUrl } from 'src/app/services/request';
import { getConfigCompany, getConfigFile } from 'src/app/store/config/asyncThunks';
import { checkAuthentication, removeAuthentication } from 'src/app/store/auth';
import { LicenseInfo } from '@mui/x-license';
import { buildAsyncRoutes, clearAsyncRoutes } from 'src/app/store/routes/asyncThunks';
import history from '@history/@history';
import {
  defaultLoginRedirect,
  defaultLogoutRedirect,
  PENDING_PARAMS_KEY,
  PENDING_URL_KEY,
} from 'src/app/configs/settingsConfig';
import { matchNavigationRoutes } from 'src/app/store/core/navigationSlice';
import { getProfileInfo } from 'src/app/store/profile/asyncThunks';
import { getProfile } from 'src/app/api/profile';
import { companyInfo } from 'src/app/api/company/company';
import { removeProfileInfo } from 'app/store/profile';
import _ from 'lodash';
import { sanitizeRedirect, sanitizeToken } from 'src/app/routing/utils/paths';
import { handleResolveMagicXchg } from 'src/app/store/auth/asyncThunk';
import { validateMagicXchg } from 'src/app/api/auth';
import { clearRoutes } from 'app/store/routes';
import { BootstrapContext } from './BootstrapContext';
import LoaderScreen from './screens/LoaderScreen';

const { Provider } = BootstrapContext;

/**
 * Estados posibles del inicializador.
 * @typedef {'loading'|'success'|'error'} InitializerState
 */

/**
 * Props del BootstrapProvider.
 *
 * @typedef {Object} BootstrapProviderProps
 *
 * @property {React.ReactNode} children
 * Contenido a renderizar tras completar el bootstrap.
 *
 * @property {Function} [onFulfilled]
 * Callback ejecutado cuando la inicialización termina correctamente.
 *
 * @property {Function} [onRejected]
 * Callback ejecutado cuando ocurre un error o autenticación inválida.
 *
 * @property {Function} [onFinally]
 * Callback ejecutado siempre al finalizar (success o error).
 *
 * @property {string[]} [setServices]
 * Lista de servicios a habilitar durante el bootstrap.
 *
 * @property {string} [jsonConfPath]
 * Path del archivo JSON con configuración de entorno (envpaths.json).
 *
 * @property {boolean} [strictMode]
 * Si está activo al cargar los servicios se interrumpirá la carga si alguno falla
 */

/**
 * Keys de servicios configurables.
 * Permiten habilitar/deshabilitar partes del bootstrap.
 */
const PROFILE_INFO_KEY = 'profileInfo';
const COMPANY_INFO_KEY = 'companyInfo';
const ENV_PHATS_INFO = 'envPhatsJSON';

/**
 * Path por defecto para archivo de configuración remota.
 */
const JSON_PATH_DEFAULT = '/envpaths.json';

/**
 * Servicios habilitados por defecto.
 */
const defaultServices = [PROFILE_INFO_KEY, COMPANY_INFO_KEY, ENV_PHATS_INFO];

/**
 * Provider encargado del arranque completo de la app.
 *
 * Flujo principal:
 *
 *  1. Configura baseURL
 *  2. Carga envpaths.json (opcional)
 *  3. Valida autenticación
 *  4. Carga profile y company info
 *  5. Construye rutas dinámicas
 *  6. Expone initializeSession vía contexto
 *  7. Renderiza LoaderScreen hasta finalizar
 *
 * @param {BootstrapProviderProps} props
 * @returns {React.ReactElement}
 */
const BootstrapProvider = ({
  children,
  onFulfilled,
  onRejected,
  onFinally,
  setServices = defaultServices,
  jsonConfPath = JSON_PATH_DEFAULT,
  buildNavbarWithRouterFilters = false,
  filterRouterByScopes = false,
  filterRouterByModules = false,
  strictMode = true,
}) => {
  const Dispatch = useDispatch();

  const [initializerState, setInitializerState] = useState('loading');
  const [processMessages, setProcessMessages] = useState([]);

  // ----------- Asignación de keys ----------
  const { config } = useSelector((state) => state.config);
  const { api } = config;
  const { muiXPro } = config.keys;

  useLayoutEffect(() => {
    if (typeof muiXPro !== 'string' || muiXPro.trim().length === 0) return;
    LicenseInfo.setLicenseKey(muiXPro);
  }, [muiXPro]);

  const isEnableService = useCallback((service) => setServices.includes(service), [setServices]);

  const finalizeSession = async (disableRedirect = false) => {
    Dispatch(removeAuthentication());
    await Dispatch(removeProfileInfo());
    await Dispatch(clearAsyncRoutes({ filterByModules: filterRouterByModules }))
      .unwrap()
      .catch((err) => {
        console.log(err);
        Dispatch(clearRoutes());
      });
    if (typeof onRejected === 'function') await onRejected();
    if (!disableRedirect) history.replace(defaultLogoutRedirect);
    return false;
  };

  const forceKillSession = async () => {
    const tokenWasVerified = await Dispatch(checkAuthentication());

    if (tokenWasVerified === true) {
      return finalizeSession(false);
    }

    return true;
  };

  /**
   * Inicializa la sesión del usuario autenticado.
   *
   * Pasos:
   *  1. Verifica token
   *  2. Limpia estado si es inválido
   *  3. Carga profile/company
   *  4. Construye rutas dinámicas
   *
   * @async
   * @returns {Promise<boolean>} true si la sesión quedó válida
   */
  const initializeSession = useCallback(
    async (disableRedirect = false) => {
      const tokenWasVerified = await Dispatch(checkAuthentication());

      if (!tokenWasVerified) {
        return finalizeSession(disableRedirect);
      }

      if (isEnableService(PROFILE_INFO_KEY))
        await Dispatch(getProfileInfo({ _request: getProfile }))
          .unwrap()
          .catch((err) => {
            console.log(err);
            if (strictMode === true) throw err;
          });

      if (isEnableService(COMPANY_INFO_KEY))
        await Dispatch(getConfigCompany({ _request: companyInfo }))
          .unwrap()
          .catch((err) => {
            console.log(err);
            if (strictMode === true) throw err;
          });

      const { filtered = [], remaining = [] } = await Dispatch(
        buildAsyncRoutes({
          filterByScopes: filterRouterByScopes,
          filterByModules: filterRouterByModules,
        })
      )
        .unwrap()
        .catch((err) => {
          console.log(err);
          if (strictMode === true) throw err;
        });

      await Dispatch(matchNavigationRoutes(buildNavbarWithRouterFilters ? filtered : remaining));

      if (typeof onFulfilled === 'function') await onFulfilled();
      if (!disableRedirect) history.replace(defaultLoginRedirect);
      return true;
    },
    [Dispatch, isEnableService, onFulfilled, onRejected]
  );

  // ---- helper centralizado para matar sesión
  const killSession = useCallback(async () => {
    await finalizeSession(true);
  }, [Dispatch, initializeSession]);

  /* ------------------------------------------------------------------------ */
  /*                           REDIRECT CHECKER                               */
  /* ------------------------------------------------------------------------ */
  const applyMagicXchg = async () => {
    const postInitialParams = sessionStorage.getItem(PENDING_PARAMS_KEY);
    sessionStorage.removeItem(PENDING_PARAMS_KEY);

    if (_.isEmpty(postInitialParams)) return false;

    const params = new URLSearchParams(postInitialParams);

    const redirect = sanitizeRedirect(params.get('redirect'));
    const tkn = sanitizeToken(params.get('token'));
    const modelId = sanitizeToken(params.get('id'));

    // console.log('redirect', redirect, '\ntkn', tkn, '\nmodelId', modelId);

    // --------- Si faltan datos no ejecutar nada
    if (!redirect || !tkn || !modelId) return false;

    try {
      // ------- limpia sesión previa antes del exchange
      Dispatch(removeAuthentication());

      // ------- Request: Devuelve nuevo token internamente
      const mXchgResult = await Dispatch(
        handleResolveMagicXchg({
          _request: validateMagicXchg,
          props: [{ token: tkn, id: modelId }],
        })
      ).unwrap();

      console.log('mXchgResult', mXchgResult);

      // ------- rehidrata sesión con nuevo token
      await initializeSession(true);
      // console.log('token', token, '\nuserScopes', userScopes);

      // -------  validar permisos sobre la ruta destino
      // if (!hasAccess(redirect)) {
      //   console.log('No tiene acceso a la ruta destino');
      //   await killSession();
      //   return;
      // }

      // ---- Si todo esta bien entonces redirecciona al path solicitado
      sessionStorage.setItem(PENDING_URL_KEY, redirect);
      return true;
    } catch (err) {
      await killSession();
      return false;
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                           BOOTSTRAP EFFECT                               */
  /* ------------------------------------------------------------------------ */

  /**
   * Ejecuta el bootstrap completo al montar el provider.
   */
  useEffect(() => {
    const initialize = async () => {
      try {
        setProcessMessages([]);
        setInitializerState('loading');
        updateBaseUrl(api?.host, api?.prefix);

        if (isEnableService(ENV_PHATS_INFO)) {
          const cfg = await Dispatch(getConfigFile({ fileDirectory: jsonConfPath })).unwrap();
          const apiCfg = cfg.api;
          updateBaseUrl(apiCfg?.host, apiCfg?.prefix);
        }

        const magicXchgApplied = await applyMagicXchg();
        if (!magicXchgApplied) await initializeSession(false);

        setInitializerState('success');
      } catch (err) {
        console.log(err);
        if (err.includes('401') || err.includes('403')) {
          finalizeSession(false);
          setInitializerState('success');
          return;
        }
        setProcessMessages(err);
        setInitializerState('error');
        await Dispatch(removeProfileInfo());
        if (typeof onRejected === 'function') onRejected();
      } finally {
        if (typeof onFinally === 'function') onFinally();
      }
    };
    initialize();
    return () => {
      if (typeof onRejected === 'function') onRejected();
      setInitializerState('loading');
    };
  }, []);

  return (
    <Provider value={{ initializeSession, initializerState, finalizeSession, forceKillSession }}>
      <LoaderScreen status={initializerState} messages={processMessages}>
        {children}
      </LoaderScreen>
    </Provider>
  );
};

export default BootstrapProvider;
